import Cookies from 'js-cookie'

class CookieService {
  // Cookie names
  static KEYS = {
    LAST_FOLDER: 'lastOpenedFolder',
    LAST_SUBFOLDER: 'lastOpenedSubfolder', 
    VIEW_MODE: 'viewMode',
    CUSTOM_VIEW_ID: 'customViewId',
    HIDE_DUPLICATES: 'hideDuplicates'
  }

  // Set cookie with default options
  static set(key, value, options = {}) {
    const defaultOptions = {
      expires: 30, // 30 days
      sameSite: 'strict',
      secure: window.location.protocol === 'https:',
      ...options
    }
    
    // Store as JSON for complex objects
    const valueToStore = typeof value === 'object' ? JSON.stringify(value) : value
    
    return Cookies.set(key, valueToStore, defaultOptions)
  }

  // Get cookie value
  static get(key) {
    const value = Cookies.get(key)
    if (!value) return null
    
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(value)
    } catch (e) {
      return value
    }
  }

  // Remove cookie
  static remove(key) {
    return Cookies.remove(key)
  }

  // Clear all BP-related cookies
  static clearAllBpCookies() {
    Object.values(this.KEYS).forEach(key => {
      this.remove(key)
    })
  }

  // Folder state management
  static setLastFolder(bpId, folder) {
    if (folder === 'compliance') return
    this.set(`${this.KEYS.LAST_FOLDER}_${bpId}`, folder)
  }

  static getLastFolder(bpId) {
    const folder = this.get(`${this.KEYS.LAST_FOLDER}_${bpId}`)
    return folder === 'compliance' ? null : folder
  }

  // Subfolder state management for custom views
  static setLastSubfolder(bpId, viewId, subfolderId) {
    const key = `${this.KEYS.LAST_SUBFOLDER}_${bpId}_${viewId}`
    this.set(key, subfolderId)
  }

  static getLastSubfolder(bpId, viewId) {
    const key = `${this.KEYS.LAST_SUBFOLDER}_${bpId}_${viewId}`
    return this.get(key)
  }

  // View mode (automatic/custom)
  static setViewMode(bpId, mode) {
    this.set(`${this.KEYS.VIEW_MODE}_${bpId}`, mode)
  }

  static getViewMode(bpId) {
    return this.get(`${this.KEYS.VIEW_MODE}_${bpId}`)
  }

  static setHideDuplicates(bpId, value) {
    const key = `${this.KEYS.HIDE_DUPLICATES}_${bpId}`
    try {
      window.localStorage.setItem(key, value ? 'true' : 'false')
    } catch (e) {
      this.set(key, value ? 'true' : 'false')
    }
  }

  static getHideDuplicates(bpId) {
    const key = `${this.KEYS.HIDE_DUPLICATES}_${bpId}`
    let raw = null
    try {
      raw = window.localStorage.getItem(key)
    } catch (e) {
      raw = null
    }
    if (raw === null || raw === undefined) {
      const legacy = this.get(key)
      if (legacy === 'false' || legacy === false) return false
      if (legacy === 'true' || legacy === true) return true
      return true
    }
    return raw === 'true'
  }

  // Custom view ID
  static setCustomViewId(bpId, viewId) {
    this.set(`${this.KEYS.CUSTOM_VIEW_ID}_${bpId}`, viewId)
  }

  static getCustomViewId(bpId) {
    return this.get(`${this.KEYS.CUSTOM_VIEW_ID}_${bpId}`)
  }

  // Clear all state for a specific BP
  static clearBpState(bpId) {
    const keysToRemove = [
      `${this.KEYS.LAST_FOLDER}_${bpId}`,
      `${this.KEYS.VIEW_MODE}_${bpId}`,
      `${this.KEYS.CUSTOM_VIEW_ID}_${bpId}`,
      `selectedFolder_${bpId}`,
      `selectedCategory_${bpId}`,
      `expandedFolders_${bpId}`
    ]
    
    // Also remove all subfolder states and custom expanded folders for this BP
    const allCookies = Cookies.get()
    Object.keys(allCookies).forEach(cookieName => {
      if (cookieName.startsWith(`${this.KEYS.LAST_SUBFOLDER}_${bpId}_`) ||
          cookieName.startsWith(`customExpandedFolders_${bpId}_`)) {
        keysToRemove.push(cookieName)
      }
    })
    
    keysToRemove.forEach(key => this.remove(key))
  }
}

export default CookieService